import { useRef, useState } from 'react';
import Dialog, { DialogError } from './Dialog.jsx';
import { api } from '../lib/api.js';
import { approvalLikelyRefused, approvalReasonText, consentingAdmin, needsTenantApproval } from '../lib/consent-approval.js';

/**
 * The operator fallback for a customer stuck in consent approval (ENTRAID-350): an "Approve tenant" button that opens
 * a confirm dialog with the facts, then POSTs { confirmConsent: true } with the tenant id and reason it displayed
 * (expectedPendingTenantId, expectedReason): the server approves only what was reviewed and answers 409 when the pending tenant changed. Renders nothing unless `isOperator` and the
 * customer needs approval. Display only: the server checks the operator role and refuses a held or locked tenant.
 *
 * @param {{ customer: object, isOperator: boolean, onApproved?: (customer: object) => void, onStale?: () => void, className?: string }} props
 */
export default function ApproveTenant({ customer, isOperator, onApproved, onStale, className = 'btn-primary text-sm' }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const buttonRef = useRef(null);
  if (isOperator !== true || !needsTenantApproval(customer)) return null;

  const name = customer.companyName || customer.customerId;

  function close() {
    if (busy) return;
    setOpen(false);
    setError(null);
  }

  async function approve() {
    setBusy(true);
    setError(null);
    try {
      await api.startConsent(customer.customerId, {
        confirmConsent: true,
        expectedPendingTenantId: customer.pendingEntraTenantId,
        ...(customer.consentApprovalReason ? { expectedReason: customer.consentApprovalReason } : {}),
      });
      setBusy(false);
      setOpen(false);
      onApproved?.(customer);
    } catch (e) {
      if (e?.status === 409 && e?.body?.details?.reason === 'pending-tenant-changed') {
        setError('The pending tenant changed; review again');
        onStale?.();
      } else {
        setError(e?.message || 'The tenant was not approved.');
      }
      setBusy(false);
    }
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={className}
        onClick={() => setOpen(true)}
        aria-label={`Approve tenant for ${name}`}
      >
        Approve tenant
      </button>
      <Dialog
        open={open}
        title={`Approve tenant for ${name}`}
        onClose={close}
        returnFocusRef={buttonRef}
        footer={(
          <>
            <button type="button" className="btn-secondary text-sm" onClick={close} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary text-sm" onClick={approve} disabled={busy}>
              {busy ? 'Approving...' : 'Approve tenant'}
            </button>
          </>
        )}
      >
        <ApprovalFacts customer={customer} />
        {approvalLikelyRefused(customer.consentApprovalReason) ? (
          <p data-testid="approve-tenant-warning">
            The server will most likely refuse this approval for that reason. Resolve the conflict first, or try and
            read the server's answer.
          </p>
        ) : (
          <p>Approving connects this tenant to {name}, confirms consent and records the approval in the audit log.</p>
        )}
        <DialogError message={error} />
      </Dialog>
    </>
  );
}

/** The approval facts: tenant id, consenting admin, reason, starter email and when it was flagged. */
export function ApprovalFacts({ customer }) {
  const admin = consentingAdmin(customer);
  const flagged = Date.parse(customer.consentApprovalReasonAt ?? '');
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[160px_1fr]" data-testid="approval-facts">
      <dt className="text-ipc-muted">Tenant ID</dt>
      <dd className="break-all font-mono text-ipc-ink">{customer.pendingEntraTenantId}</dd>
      <dt className="text-ipc-muted">Consenting admin</dt>
      <dd className="break-all text-ipc-ink">{admin ?? 'Not recorded'}</dd>
      <dt className="text-ipc-muted">Why it stopped</dt>
      <dd className="text-ipc-ink">{approvalReasonText(customer.consentApprovalReason)}</dd>
      {customer.consentStarterEmail ? (
        <>
          <dt className="text-ipc-muted">Started by</dt>
          <dd className="break-all text-ipc-ink">{customer.consentStarterEmail}</dd>
        </>
      ) : null}
      {Number.isNaN(flagged) ? null : (
        <>
          <dt className="text-ipc-muted">Flagged</dt>
          <dd className="text-ipc-ink">{new Date(flagged).toLocaleString()}</dd>
        </>
      )}
    </dl>
  );
}
